// Envía reportes de errores del cliente al endpoint nginx /_log/client.
// nginx loguea el body como CLIENT_ERROR a stdout, así Render lo captura.
//
// - En DEV no envía (sólo console.error) para no ensuciar.
// - Dedupe simple: ignora repeticiones de la misma firma dentro de la ventana.
// - keepalive: true para no perder reports al cerrar pestaña.
// - Redaction básica de query strings y campos sensibles para evitar PII.

type ClientErrorType =
  | 'react'
  | 'http'
  | 'window.error'
  | 'unhandledrejection'
  | 'tracking-error'
  | 'form-submit-error';

interface ClientErrorPayload {
  type: ClientErrorType;
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
  method?: string;
  status?: number;
  ua?: string;
  ts?: string;
  [k: string]: unknown;
}

const ENDPOINT = '/_log/client';
const DEDUPE_WINDOW_MS = 30_000;
const MAX_DEDUPE_ENTRIES = 50;
const recent = new Map<string, number>();

// Robusto: aunque ``import.meta?.env?.DEV`` sea undefined por algún motivo
// (puppeteer headless, contexto de worker, transform raro), si el hostname
// es localhost / 127.0.0.1 / *.docker.internal, también es dev y NO debemos
// llegar al fetch (el endpoint ``/_log/client`` solo existe en nginx prod).
const isDev = (() => {
  try {
    if (import.meta?.env?.DEV) return true;
  } catch {
    /* fallthrough */
  }
  try {
    if (typeof location !== 'undefined') {
      const host = location.hostname;
      if (
        host === 'localhost' ||
        host === '127.0.0.1' ||
        host === '0.0.0.0' ||
        host.endsWith('.docker.internal') ||
        host.endsWith('.local')
      ) {
        return true;
      }
    }
  } catch {
    /* ignore */
  }
  return false;
})();

const SENSITIVE_KEYS = /(password|secret|token|authorization|cookie)/i;

const stripQuery = (url: string | undefined): string | undefined => {
  if (!url) return url;
  const idx = url.indexOf('?');
  return idx >= 0 ? url.slice(0, idx) : url;
};

const redact = <T extends Record<string, unknown>>(obj: T): T => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (SENSITIVE_KEYS.test(k)) {
      out[k] = '[REDACTED]';
    } else if (typeof v === 'string' && v.length > 4_000) {
      out[k] = v.slice(0, 4_000) + '…[truncated]';
    } else {
      out[k] = v;
    }
  }
  return out as T;
};

const dedupeKey = (p: ClientErrorPayload): string =>
  `${p.type}|${p.status ?? ''}|${stripQuery(p.url) ?? ''}|${p.message?.slice(0, 200) ?? ''}`;

const shouldSkip = (key: string): boolean => {
  const now = Date.now();
  // Limpieza barata para no crecer indefinido.
  if (recent.size > MAX_DEDUPE_ENTRIES) {
    for (const [k, t] of recent) {
      if (now - t > DEDUPE_WINDOW_MS) recent.delete(k);
    }
  }
  const last = recent.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  recent.set(key, now);
  return false;
};

export function reportClientError(input: ClientErrorPayload): void {
  const payload: ClientErrorPayload = redact({
    ts: new Date().toISOString(),
    ua: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    ...input,
    url: stripQuery(input.url),
  });

  if (isDev) {
    // En dev sólo a consola: el endpoint /_log/client lo sirve nginx en prod.
     
    console.error('[clientErrorReporter]', payload);
    return;
  }

  if (shouldSkip(dedupeKey(payload))) return;

  try {
    const body = JSON.stringify(payload);
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
      credentials: 'omit',
    }).catch(() => {
      // Silenciar: no queremos bucles si el reporte falla.
    });
  } catch {
    // JSON.stringify puede fallar (cycles); ignorar.
  }
}

export function installGlobalErrorReporters(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (e: ErrorEvent) => {
    reportClientError({
      type: 'window.error',
      message: e.message || 'window.error',
      stack: e.error?.stack,
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    const message =
      typeof reason === 'string'
        ? reason
        : reason?.message
          ? String(reason.message)
          : 'unhandledrejection';
    reportClientError({
      type: 'unhandledrejection',
      message,
      stack: reason?.stack,
      url: typeof location !== 'undefined' ? location.href : undefined,
    });
  });
}
